"use client";

import { useCallback, useState, type KeyboardEvent } from "react";

/** A numeric entry with − / + buttons and NO native spinner arrows. The value is
 *  a string (kept controlled by the parent's config/state). Use this everywhere a
 *  number is entered — NEVER a bare `<input type="number">` (no native spinners,
 *  ever).
 *
 *  `step` drives the +/- increment and the displayed precision (an integer step
 *  rounds to whole numbers; `0.25` keeps two decimals). `min`/`max` clamp the
 *  buttons (typed input is only sanitized, so partial entry still works).
 *  `allowNegative` permits a leading "−". `padDecimals` always shows the step's
 *  decimal places (e.g. `5` → `5.00` for a 0.25 step) while the field isn't being
 *  edited. `onBlur`/`onKeyDown` are passed through for fields that save on blur or
 *  act on Enter. */
export function NumberStepper({ value, onChange, step = 1, min, max, width = 72, inputMode = "numeric", disabled = false, allowNegative = false, padDecimals = false, placeholder, onBlur, onKeyDown, className = "" }: {
  value: string | undefined;
  onChange: (v: string) => void;
  step?: number;
  min?: number;
  max?: number;
  width?: number;
  inputMode?: "numeric" | "decimal";
  disabled?: boolean;
  allowNegative?: boolean;
  padDecimals?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** Extra classes on the outer wrapper — e.g. `!border-red` for invalid values. */
  className?: string;
}) {
  const [focused, setFocused] = useState(false);

  const clamp = useCallback((n: number) => {
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    return n;
  }, [min, max]);

  const decimals = (String(step).split(".")[1] || "").length;
  const fmt = (n: number) => (decimals > 0 ? n.toFixed(decimals) : String(Math.round(n)));

  const bump = (dir: 1 | -1) => {
    const cur = parseFloat(value ?? "") || 0;
    onChange(fmt(clamp(cur + dir * step)));
  };

  const sanitize = (s: string) => {
    const base = inputMode === "decimal" ? s.replace(/[^0-9.]/g, "") : s.replace(/[^0-9]/g, "");
    return allowNegative && s.trimStart().startsWith("-") ? "-" + base : base;
  };

  // With padDecimals, show a fixed number of decimals (from `step`) while the
  // field isn't focused; show the raw string while focused so typing isn't fought.
  const parsed = parseFloat(value ?? "");
  const hasNum = value != null && value !== "" && !isNaN(parsed);
  const display = padDecimals && !focused && hasNum ? parsed.toFixed(decimals) : (value ?? "");

  const btn = "px-2 py-0.5 text-muted enabled:hover:text-foreground enabled:hover:bg-surface-light enabled:cursor-pointer select-none";

  return (
    <div className={`inline-flex items-center rounded border border-border overflow-hidden ${disabled ? "opacity-40" : ""} ${className}`}>
      <button type="button" tabIndex={-1} disabled={disabled} onClick={() => bump(-1)} className={btn}>−</button>
      <input
        type="text"
        inputMode={inputMode}
        value={display}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(sanitize(e.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (padDecimals && hasNum) onChange(fmt(clamp(parsed)));
          onBlur?.();
        }}
        onKeyDown={onKeyDown}
        style={{ width }}
        className="bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-sm text-right placeholder-muted focus:outline-none"
      />
      <button type="button" tabIndex={-1} disabled={disabled} onClick={() => bump(1)} className={btn}>+</button>
    </div>
  );
}
